import React from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const STAGE_LABELS = {
  new: 'New Lead',
  contacted: 'Contacted',
  interested: 'Interested',
  test_drive: 'Test Drive',
  negotiation: 'Negotiation',
  finance_pending: 'Finance Approval',
  dp_pending: 'DP Pending',
  won: 'Won',
  lost: 'Lost'
};

const PRIORITY_STYLES = {
  hot: { bg: 'bg-red-100', text: 'text-red-700', label: '🔥 Hot' },
  warm: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '🌡️ Warm' },
  cold: { bg: 'bg-blue-100', text: 'text-blue-700', label: '❄️ Cold' }
};

// Format date to DD-MM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

// Format datetime for activity feed
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return `${day}/${month}/${year}, ${hour12}:${minutes} ${ampm}`;
  } catch {
    return dateStr;
  }
};

// Format next follow-up date with day name and time
const formatNextFollowUp = (dateStr, timeStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayName = days[date.getDay()];
    const dayNum = date.getDate();
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();
    
    let timeDisplay = '';
    if (timeStr) {
      // Format time as HH:MM AM/PM
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      timeDisplay = `${hour12}:${minutes || '00'} ${ampm}, `;
    }
    
    return `${timeDisplay}${dayNum} ${monthName} ${year} (${dayName})`;
  } catch {
    return dateStr;
  }
};

// Get short date info for activity card
const getActivityDate = (dateStr) => {
  if (!dateStr) return { day: '', weekday: '' };
  try {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return {
      day: date.getDate(),
      weekday: days[date.getDay()]
    };
  } catch {
    return { day: '', weekday: '' };
  }
};

// Calculate days since created
const getDaysSince = (dateStr) => {
  if (!dateStr) return 0;
  const created = new Date(dateStr);
  const now = new Date();
  const diffTime = Math.abs(now - created);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

function LeadDetailView({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState('followup');
  const [followUpHistory, setFollowUpHistory] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  
  // Follow-up form state
  const [followUpForm, setFollowUpForm] = React.useState({
    call_status: '',
    followup_status: '',
    sub_lead_status: '',
    next_follow_up_date: '',
    next_follow_up_time: '',
    remarks: ''
  });

  React.useEffect(() => {
    fetchLeadData();
  }, [id]);

  const fetchLeadData = async () => {
    setLoading(true);
    try {
      const [leadRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/leads/${id}`, getAuthHeader()),
        axios.get(`${BACKEND_URL}/api/leads/${id}/follow-up-history`, getAuthHeader())
      ]);
      setLead(leadRes.data);
      setFollowUpHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Failed to load lead details');
    }
    setLoading(false);
  };

  const handleFollowUpSubmit = async () => {
    if (!followUpForm.call_status) {
      toast.error('Please select Call Status');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads/${id}/follow-up`, followUpForm, getAuthHeader());
      toast.success('Follow-up saved successfully!');
      
      // Reset form
      setFollowUpForm({
        call_status: '',
        followup_status: '',
        sub_lead_status: '',
        next_follow_up_date: '',
        next_follow_up_time: '',
        remarks: ''
      });
      
      // Refresh data
      fetchLeadData();
    } catch (error) {
      console.error('Error saving follow-up:', error);
      toast.error('Failed to save follow-up');
    }
    setSaving(false);
  };

  const handleWhatsApp = () => {
    const phone = lead.whatsapp || lead.mobile;
    const message = encodeURIComponent(
      `Hi ${lead.first_name}, this is regarding your enquiry for ${lead.interested_model || 'our vehicles'}. Please let us know if you have any questions.`
    );
    { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?phone=91${phone}&text=${message}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
  };

  const handleCall = () => {
    window.open(`tel:${lead.mobile}`, '_self');
  };

  const handleConvertToBooking = async () => {
    if (!window.confirm('Convert this lead to a booking?')) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads/${id}/convert`, {}, getAuthHeader());
      toast.success(`Booking #${response.data.booking?.booking_no} created!`);
      navigate('/leads');
    } catch (error) {
      console.error('Error converting to booking:', error);
      toast.error('Failed to convert to booking');
    }
  };

  if (loading) {
    return (
      <LeadsLayout user={user} onLogout={onLogout} title="Lead Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </LeadsLayout>
    );
  }

  if (!lead) {
    return (
      <LeadsLayout user={user} onLogout={onLogout} title="Lead Details">
        <div className="text-center py-16">
          <p className="text-gray-500">Lead not found</p>
          <button onClick={() => navigate('/leads/list')} className="mt-4 text-emerald-600 hover:underline">
            ← Back to Leads
          </button>
        </div>
      </LeadsLayout>
    );
  }

  const priorityStyle = PRIORITY_STYLES[lead.priority] || PRIORITY_STYLES.warm;
  const daysSince = getDaysSince(lead.created_at);

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Lead Details">
      <div className="flex gap-6">
        {/* LEFT COLUMN - Customer Details */}
        <div className="w-1/3 space-y-4">
          {/* Customer Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            {/* Status Badges */}
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                lead.stage === 'won' ? 'bg-green-100 text-green-700' :
                lead.stage === 'lost' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {STAGE_LABELS[lead.stage] || lead.stage}
              </span>
              {lead.next_follow_up_date && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                  Follow-up on {formatDate(lead.next_follow_up_date)}
                </span>
              )}
            </div>

            {/* Phone Number */}
            <div className="text-2xl font-bold text-gray-800 mb-2">
              {lead.mobile}
            </div>

            {/* Name with Edit */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl font-semibold text-gray-700">
                {lead.salutation} {lead.first_name} {lead.last_name || ''}
              </span>
              <button 
                onClick={() => navigate(`/leads/edit/${id}`)}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                title="Edit Lead"
              >
                ✏️
              </button>
            </div>

            {/* Location */}
            {(lead.city || lead.state) && (
              <div className="flex items-center gap-2 text-gray-500 mb-3">
                <span>📍</span>
                <span>{lead.city}{lead.city && lead.state && ', '}{lead.state}</span>
              </div>
            )}

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                ACTIVE SINCE {daysSince} DAY{daysSince !== 1 ? 'S' : ''}
              </span>
              {lead.lead_source && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                  {lead.lead_source.toUpperCase()}
                </span>
              )}
              <span className={`px-2 py-1 rounded text-xs ${priorityStyle.bg} ${priorityStyle.text}`}>
                {priorityStyle.label}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={handleWhatsApp}
                className="flex flex-col items-center gap-1 p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                title="WhatsApp"
              >
                <span className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  </svg>
                </span>
                <span className="text-xs text-gray-600">WhatsApp</span>
              </button>

              <button
                onClick={handleCall}
                className="flex flex-col items-center gap-1 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                title="Call"
              >
                <span className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  📞
                </span>
                <span className="text-xs text-gray-600">Call</span>
              </button>

              <button
                onClick={() => {
                  const text = `Lead: ${lead.first_name} ${lead.last_name || ''}\nPhone: ${lead.mobile}\nModel: ${lead.interested_model || 'N/A'}`;
                  navigator.clipboard.writeText(text);
                  toast.success('Lead details copied!');
                }}
                className="flex flex-col items-center gap-1 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Share"
              >
                <span className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600">
                  🔗
                </span>
                <span className="text-xs text-gray-600">Share</span>
              </button>

              <div className="flex-1"></div>

              {lead.stage !== 'won' && lead.stage !== 'lost' && (
                <button
                  onClick={handleConvertToBooking}
                  className="px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 font-medium flex items-center gap-2"
                >
                  📋 Book
                </button>
              )}
            </div>
          </div>

          {/* Inquiries Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Inquiries</h3>
              <button 
                onClick={() => navigate(`/leads/edit/${id}`)}
                className="px-3 py-1 border border-red-500 text-red-500 rounded text-sm hover:bg-red-50"
              >
                Add New
              </button>
            </div>

            {lead.interested_model ? (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">
                      {lead.interested_brand} {lead.interested_model} {lead.interested_variant}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(lead.created_at)}
                      {lead.lead_source && (
                        <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">
                          {lead.lead_source}
                        </span>
                      )}
                    </p>
                  </div>
                  <button 
                    onClick={() => navigate(`/leads/edit/${id}`)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                  >
                    ✏️
                  </button>
                </div>
                {lead.stage !== 'won' && lead.stage !== 'lost' && (
                  <button 
                    onClick={handleConvertToBooking}
                    className="mt-3 text-blue-600 hover:underline text-sm font-medium"
                  >
                    Book →
                  </button>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No vehicle inquiry added</p>
            )}
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-gray-800 mb-4">Additional Details</h3>
            <div className="space-y-3 text-sm">
              {lead.email && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">📧</span>
                  <span className="text-gray-600">{lead.email}</span>
                </div>
              )}
              {lead.assigned_to && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">👤</span>
                  <span className="text-gray-600">Sales Rep: {lead.assigned_to}</span>
                </div>
              )}
              {lead.preferred_financier && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">🏦</span>
                  <span className="text-gray-600">Financier: {lead.preferred_financier}</span>
                </div>
              )}
              {lead.budget_range && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">💰</span>
                  <span className="text-gray-600">Budget: {lead.budget_range}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Activity & Follow-up */}
        <div className="w-2/3 space-y-4">
          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('followup')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === 'followup'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Follow Up
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === 'history'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Activity History
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                    {followUpHistory.length}
                  </span>
                </button>
              </div>
            </div>

            {/* Follow-up Form */}
            {activeTab === 'followup' && (
              <div className="p-6 space-y-5">
                {/* Call Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFollowUpForm(prev => ({ ...prev, call_status: 'Connected' }))}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                        followUpForm.call_status === 'Connected'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      Connected
                    </button>
                    <button
                      type="button"
                      onClick={() => setFollowUpForm(prev => ({ ...prev, call_status: 'Not Connected' }))}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                        followUpForm.call_status === 'Not Connected'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      Not Connected
                    </button>
                  </div>
                </div>

                {/* Follow-up Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Follow-up Status
                  </label>
                  <select
                    value={followUpForm.followup_status}
                    onChange={(e) => setFollowUpForm(prev => ({ ...prev, followup_status: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Select --</option>
                    <option value="Spoke to Customer">Spoke to Customer</option>
                    <option value="Visit Confirmed">Visit Confirmed</option>
                    <option value="Call Back Later">Call Back Later</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Wrong Number">Wrong Number</option>
                    <option value="Switched Off">Switched Off</option>
                    <option value="Not Reachable">Not Reachable</option>
                  </select>
                </div>

                {/* Sub Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub Status
                  </label>
                  <select
                    value={followUpForm.sub_lead_status}
                    onChange={(e) => setFollowUpForm(prev => ({ ...prev, sub_lead_status: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Select --</option>
                    <option value="Hot Lead - Visit Confirmed">Hot Lead - Visit Confirmed</option>
                    <option value="Warm Lead - Interested">Warm Lead - Interested</option>
                    <option value="Cold Lead - Follow Later">Cold Lead - Follow Later</option>
                    <option value="EMI Related">EMI Related</option>
                    <option value="Exchange Related">Exchange Related</option>
                    <option value="Price Concern">Price Concern</option>
                    <option value="Comparing Options">Comparing Options</option>
                  </select>
                </div>

                {/* Next Follow-up Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Next Follow-up Date
                    </label>
                    <input
                      type="date"
                      value={followUpForm.next_follow_up_date}
                      onChange={(e) => setFollowUpForm(prev => ({ ...prev, next_follow_up_date: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time
                    </label>
                    <input
                      type="time"
                      value={followUpForm.next_follow_up_time}
                      onChange={(e) => setFollowUpForm(prev => ({ ...prev, next_follow_up_time: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remarks
                  </label>
                  <textarea
                    value={followUpForm.remarks}
                    onChange={(e) => setFollowUpForm(prev => ({ ...prev, remarks: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add notes about this follow-up..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleFollowUpSubmit}
                  disabled={saving}
                  className="w-full py-3 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
                >
                  {saving ? 'Saving...' : 'Save Follow-up'}
                </button>
              </div>
            )}

            {/* Activity History */}
            {activeTab === 'history' && (
              <div className="p-6">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  📋 Activity Feed
                </h3>
                
                {followUpHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No follow-up history yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {followUpHistory.map((item, index) => {
                      const activityDate = getActivityDate(item.created_at || item.timestamp);
                      const nextFollowUp = formatNextFollowUp(item.next_follow_up_date, item.next_follow_up_time);
                      
                      return (
                        <div key={item.id || index} className="flex gap-4">
                          {/* Date Column */}
                          <div className="flex-shrink-0 w-16 text-center">
                            <div className="text-2xl font-bold text-gray-800">{activityDate.day}</div>
                            <div className="text-sm text-gray-500">{activityDate.weekday}</div>
                          </div>
                          
                          {/* Activity Card */}
                          <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">
                                Follow up added by <span className="text-blue-600">{item.created_by || 'System'}</span>
                              </span>
                              <span className="text-xs text-gray-400">
                                {formatDateTime(item.created_at || item.timestamp)}
                              </span>
                            </div>
                            
                            {/* Details */}
                            <div className="space-y-2 text-sm">
                              {(item.remarks || item.description) && (
                                <div className="flex">
                                  <span className="text-gray-500 w-32">Comment:</span>
                                  <span className="text-gray-700">{item.remarks || item.description}</span>
                                </div>
                              )}
                              
                              <div className="flex">
                                <span className="text-gray-500 w-32">Call Status:</span>
                                <span className={`font-medium ${
                                  item.call_status === 'Connected' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {item.call_status || '-'}
                                </span>
                              </div>
                              
                              {item.followup_status && (
                                <div className="flex">
                                  <span className="text-gray-500 w-32">Follow-Up Status:</span>
                                  <span className="text-gray-700">{item.followup_status}</span>
                                </div>
                              )}
                              
                              {item.sub_lead_status && (
                                <div className="flex">
                                  <span className="text-gray-500 w-32">Lead Status:</span>
                                  <span className="text-gray-700">{item.sub_lead_status}</span>
                                </div>
                              )}
                              
                              {/* Next Follow-up - Important! */}
                              {nextFollowUp && (
                                <div className="flex mt-2 pt-2 border-t border-gray-100">
                                  <span className="text-gray-500 w-32">Next Followup:</span>
                                  <span className="text-orange-600 font-medium">
                                    🗓️ {nextFollowUp}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="mt-6">
        <button
          onClick={() => navigate('/leads/list')}
          className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
        >
          ← Back to All Leads
        </button>
      </div>
    </LeadsLayout>
  );
}

export default LeadDetailView;
